package fr.frux.shared.dto

import kotlinx.serialization.Serializable

/**
 * Demande d'un code de connexion par email (ADR 0012). La réponse est 200 quoi qu'il arrive :
 * révéler si l'adresse a un compte n'apporte rien à l'utilisateur légitime, qui attend son code.
 *
 * [purpose] borne ce que le code prouvera à la vérification (#274) : `"LOGIN"` (défaut,
 * rétrocompatible) ou `"DELETE_ACCOUNT"`. Un code LOGIN ne peut pas confirmer une suppression de
 * compte, et inversement — c'est le serveur qui l'impose, ce champ ne fait que le lui dire.
 */
@Serializable
data class RequestCodeRequest(
    val email: String,
    val purpose: String? = null,
)

/**
 * [devCode] n'est renseigné que lorsque l'API tourne avec l'écho de codes activé (LOCAL/TEST,
 * fournisseur d'email `log`) : c'est ce qui permet au smoke, aux tests de contrat et au dev
 * de boucler sans boîte mail. En production le champ est toujours nul.
 */
@Serializable
data class RequestCodeResponse(
    val devCode: String? = null,
)

/**
 * Vérification du code reçu (ADR 0012). L'inscription et la connexion sont le même geste :
 * [username] n'est requis que si l'adresse n'a pas encore de compte — le serveur répond
 * `USERNAME_REQUIRED` dans ce cas, et l'UI ne demande le pseudo qu'à ce moment-là.
 */
@Serializable
data class VerifyCodeRequest(
    val email: String,
    val code: String,
    val username: String? = null,
)

/**
 * Reconfirmation par code OTP d'un effacement de compte (#172, ADR 0011/0012). L'appelant est déjà
 * porteur d'un JWT valide ; ce champ existe parce qu'un geste irréversible ne doit pas tenir au
 * seul vol d'un jeton — le code prouve l'accès à la boîte mail, comme la connexion.
 */
@Serializable
data class DeleteAccountRequest(
    val code: String,
)

/**
 * Sessions révocables (#26, ADR 0019) : plus de champ `token` unique — [accessToken] (JWT, 15 min,
 * `expiresIn` en secondes) et [refreshToken] (secret opaque `frxr1_...`, 90 j glissants) ont des
 * durées de vie et des usages distincts. Pas d'alias `token` : en garder un ferait vivre deux
 * vérités et masquerait un client resté sur l'ancien contrat.
 */
@Serializable
data class AuthResponse(
    val accessToken: String,
    val refreshToken: String,
    val expiresIn: Long,
    val user: UserDto,
)

/** Corps de `POST /auth/refresh` (#26, ADR 0019) : le secret opaque, jamais l'ancien access token. */
@Serializable
data class RefreshRequest(
    val refreshToken: String,
)

/** Corps de `POST /auth/logout` (#26, ADR 0019) : révoque la famille entière (l'appareil). */
@Serializable
data class LogoutRequest(
    val refreshToken: String,
)

@Serializable
data class ErrorResponse(
    val message: String,
    val code: String? = null,
    val requestId: String? = null,
)
